import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setResponse } from "../Response/responseSlice";
import { setToast, setLoading, setShowBackdrop } from "../uiSlice";
import { createDelegacion, deleteDelegacion, getDelegacionesUsuario } from "../../Services/delegacion_service";
import { getDistritos, getRegiones, getSucursales } from "../../Services/byte_service";
import { fetchCarteraSucursal, setDistritos, setRegiones, setSucursales } from "../Byte/byteSlice";
import { fetchDistribucionItems, setDistribucion } from "../Distribucion/distribucionSlice";

const initialState = {
    delegaciones: [],
    sucursalesAsignadas: [],
    regionesAsignadas: [],
    distritosAsignados: [],
}

export const delegacionSlice = createSlice({
    name: 'delegacion',
    initialState,
    reducers: {
        setDelegaciones: (state, action) => {
            state.delegaciones = action.payload;
        },
        setSucursalesAsignadas: (state, action) => {
            state.sucursalesAsignadas = action.payload;
        },
        setRegionesAsignadas: (state, action) => {
            state.regionesAsignadas = action.payload;
        },
        setDistritosAsignados: (state, action) => {
            state.distritosAsignados = action.payload;
        },
        resetDelegacion: () => initialState,
    }
});

export const fetchDelegaciones = createAsyncThunk(
    'delegacion/fetchDelegacion',
    async (data, { dispatch }) => {
        const delegacionRes = await getDelegacionesUsuario(data);
        if (delegacionRes) {
            await dispatch(setDelegaciones(delegacionRes));
            await dispatch(fetchDistribucionItems({ ...data, ...delegacionRes[0] }));
        } else {
            await dispatch(setDelegaciones([]))
        }
    }
);

export const fetchSucursalesAsignadas = createAsyncThunk(
    'delegacion/fetchSucursalesAsignadas',
    async (data, { dispatch }) => {
        const sucursalesRes = await getSucursales();
        const delegacionRes = await getDelegacionesUsuario(data);
        const delegacionesAsg = delegacionRes ? delegacionRes.find(del => del.codDistribucion === 'sucursal')?.delegaciones : [];
        const sucuAsg = delegacionesAsg ? sucursalesRes.filter(sucu => delegacionesAsg.find(del => sucu.id === del.idDistribucionByte)) : null;
        if (sucuAsg) {
            await dispatch(setSucursalesAsignadas(sucuAsg));
            await dispatch(setSucursales(sucursalesRes.filter(sucu => sucuAsg.indexOf(sucu) == -1)));
        } else {
            await dispatch(setSucursalesAsignadas(null));
            await dispatch(setSucursales(sucursalesRes));
        }
        
        if (sucuAsg && sucuAsg.length === 1 && data?.cartera) {
            await dispatch(fetchCarteraSucursal({ idSucursal: sucuAsg[0]?.id }));
        }
    }
);

export const fetchRegionesAsignadas = createAsyncThunk(
    'delegacion/fetchRegionesAsignadas',
    async (data, { dispatch }) => {
        const regionesRes = await getRegiones();
        const delegacionRes = await getDelegacionesUsuario(data);
        const delegacionesAsg = delegacionRes ? delegacionRes.find(del => del.codDistribucion === 'region')?.delegaciones : [];
        const regionesAsg = delegacionesAsg ? regionesRes.filter(reg => delegacionesAsg.find(del => reg.id === del.idDistribucionByte)) : null;
        if (regionesAsg) {
            await dispatch(setRegionesAsignadas(regionesAsg));
            await dispatch(setRegiones(regionesRes.filter(reg => regionesAsg.indexOf(reg) == -1)));
        } else {
            await dispatch(setRegionesAsignadas(null));
            await dispatch(setRegiones(regionesRes));
        }
    }
);

export const fetchDistritosAsignados = createAsyncThunk(
    'delegacion/fetchDistritosAsignados',
    async (data, { dispatch }) => {
        const distritosRes = await getDistritos();
        const delegacionRes = await getDelegacionesUsuario(data);
        const delegacionesAsg = delegacionRes ? delegacionRes.find(del => del.codDistribucion === 'distrito')?.delegaciones : [];
        const distritosAsg = delegacionesAsg ? distritosRes.filter(dist => delegacionesAsg.find(del => dist.id === del.idDistribucionByte)) : null;
        if (distritosAsg) {
            const filterDist = distritosRes.filter(dist => distritosAsg.indexOf(dist) == -1);
            await dispatch(setDistritosAsignados(distritosAsg));
            await dispatch(setDistritos(filterDist));
        } else {
            await dispatch(setDistritosAsignados(null));
            await dispatch(setDistritos(distritosRes));
        }
    }
);

export const fetchDelegacionesCasosEspeciales = createAsyncThunk(
    'delegacion/fetchDelegacionesCasosEspeciales',
    async (data, { dispatch }) => {
        if (data && !data?.noBackdrop)
            dispatch(setShowBackdrop(true));

        const [delegacionRes, distritosRes, regionesRes, sucursalesRes] = await Promise.all([
            getDelegacionesUsuario(data),
            getDistritos(),
            getRegiones(),
            getSucursales()
        ]);

        dispatch(setDelegaciones(delegacionRes));
        dispatch(setDistritos(distritosRes));
        dispatch(setRegiones(regionesRes));
        dispatch(setSucursales(sucursalesRes));

        if (delegacionRes?.[0]) {
            const { delegaciones, ...distribucionData } = delegacionRes[0];
            dispatch(setDistribucion(distribucionData));

            if (distritosRes && delegacionRes[0].codDistribucion === 'distrito') {
                const delegacionesAsg = delegacionRes ? delegacionRes.find(del => del.codDistribucion === 'distrito')?.delegaciones : [];
                const distritosAsg = delegacionesAsg ? distritosRes.filter(dist => delegacionesAsg.find(del => dist.id === del.idDistribucionByte)) : null;
                if (distritosAsg) {
                    await dispatch(setDistritosAsignados(distritosAsg));
                }
            }

            if (regionesRes && delegacionRes[0].codDistribucion === 'region') {
                const delegacionesAsg = delegacionRes ? delegacionRes.find(del => del.codDistribucion === 'region')?.delegaciones : [];
                const regionesAsg = delegacionesAsg ? regionesRes.filter(reg => delegacionesAsg.find(del => reg.id === del.idDistribucionByte)) : null;
                if (regionesAsg) {
                    await dispatch(setRegionesAsignadas(regionesAsg));
                }
            }

            if (sucursalesRes && delegacionRes[0].codDistribucion === 'sucursal') {
                const delegacionesAsg = delegacionRes ? delegacionRes.find(del => del.codDistribucion === 'sucursal')?.delegaciones : [];
                const sucuAsg = delegacionesAsg ? sucursalesRes.filter(sucu => delegacionesAsg.find(del => sucu.id === del.idDistribucionByte)) : null;
                if (sucuAsg) {
                    await dispatch(setSucursalesAsignadas(sucuAsg));
                }
            }
        }

        if (data && !data?.noBackdrop)
            dispatch(setShowBackdrop(false));
    }
);

export const addDelegacion = createAsyncThunk(
    'delegacion/create',
    async (data, { dispatch }) => {
        console.log(data);
        dispatch(setLoading(true))
        const delegacionRes = await createDelegacion(data);
        dispatch(setResponse(delegacionRes));
        dispatch(fetchDistribucionItems(data));
        dispatch(setLoading(false));
        dispatch(setToast(true));
        setTimeout(() => {
            dispatch(setToast(false));
        }, 3000);
    }
);

export const DeleteDelegacion = createAsyncThunk(
    'delegacion/delete',
    async (data, { dispatch }) => {
        const delegacionRes = await deleteDelegacion(data);
        dispatch(setResponse(delegacionRes));
        dispatch(setToast(true));
        dispatch(fetchDelegaciones(data));
        dispatch(fetchDistribucionItems(data));
        setTimeout(() => {
            dispatch(setToast(false));
        }, 3000);
    }
);

export const { setDelegaciones, setSucursalesAsignadas, setRegionesAsignadas, setDistritosAsignados, resetDelegacion } = delegacionSlice.actions;
export default delegacionSlice.reducer;